/**
 * Client-side mirror of API cash discount math.
 * Apply only to balance due (pre-tip).
 */

export type CashDiscountMode = 'card_surcharge' | 'cash_discount';

export type CashDiscountResult = {
  originalTotal: number;
  cashTotal: number;
  cardTotal: number;
  discountAmount: number;
  discountRate: number;
  discountLabel: string;
  mode: CashDiscountMode;
};

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

export function calculateCashDiscount(
  balanceDue: number,
  store: {
    cashDiscountEnabled?: boolean | null;
    cashDiscountRate?: number | string | null;
    cashDiscountMode?: string | null;
    cashDiscountLabel?: string | null;
  },
): CashDiscountResult {
  const amount = Number(balanceDue) || 0;
  const enabled = Boolean(store.cashDiscountEnabled);
  const rate = Math.max(0, Number(store.cashDiscountRate ?? 0) || 0);
  const mode: CashDiscountMode =
    store.cashDiscountMode === 'cash_discount' ? 'cash_discount' : 'card_surcharge';
  const discountLabel = (store.cashDiscountLabel ?? 'Cash Discount').trim() || 'Cash Discount';

  if (!enabled || rate <= 0 || amount <= 0) {
    return {
      originalTotal: amount,
      cashTotal: amount,
      cardTotal: amount,
      discountAmount: 0,
      discountRate: 0,
      discountLabel: '',
      mode,
    };
  }

  const discountAmount = roundMoney(amount * rate);

  if (mode === 'card_surcharge') {
    return {
      originalTotal: amount,
      cashTotal: amount,
      cardTotal: roundMoney(amount + discountAmount),
      discountAmount,
      discountRate: rate,
      discountLabel: discountLabel || 'Card Surcharge',
      mode,
    };
  }

  return {
    originalTotal: amount,
    cashTotal: roundMoney(Math.max(amount - discountAmount, 0)),
    cardTotal: amount,
    discountAmount,
    discountRate: rate,
    discountLabel,
    mode,
  };
}

export function amountDueForMethod(result: CashDiscountResult, method: string): number {
  if (method === 'cash' || method === 'store_credit') return result.cashTotal;
  if (method === 'card') return result.cardTotal;
  return result.originalTotal;
}

export function terminalEntryAmount(
  result: CashDiscountResult,
  terminalAppliesSurcharge?: boolean | null,
): { amount: number; label: string; detail: string } {
  const applies = terminalAppliesSurcharge !== false;
  if (!result.discountRate) {
    return { amount: result.originalTotal, label: 'ENTER INTO TERMINAL', detail: '' };
  }
  if (applies) {
    return {
      amount: result.cashTotal,
      label: 'ENTER INTO TERMINAL',
      detail: `Terminal adds the ${(result.discountRate * 100).toFixed(1)}% surcharge`,
    };
  }
  return {
    amount: result.cardTotal,
    label: 'ENTER INTO TERMINAL',
    detail: 'Amount already includes card surcharge — do not let terminal add more',
  };
}
